import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { extractError } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Lock, Mail, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Bem-vindo à plataforma Prumo");
      nav("/");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] flex">
      {/* Left form */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-24 max-w-2xl">
        <div className="flex items-center gap-3 mb-16">
          <div className="w-10 h-10 rounded bg-[#0B1F3A] flex items-center justify-center font-display font-black text-xl text-[#FFD400]">P</div>
          <div>
            <div className="font-display font-black tracking-tight text-lg leading-none">PRUMO</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#6B7280] mt-1">Gestão de Vistorias</div>
          </div>
        </div>

        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#0B1F3A] mb-3 font-bold">
            <span className="inline-block px-2 py-0.5 bg-[#FFD400] text-[#0B1F3A] rounded-sm">Acesso restrito</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[1.05]">
            Gestão de operações<br /><span className="text-[#6B7280]">para vistorias imobiliárias.</span>
          </h1>
          <p className="mt-4 text-sm text-[#6B7280] max-w-md">
            Plataforma multiempresa e multicidade. Entre com suas credenciais para continuar.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5 max-w-md">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-bold">E-mail</label>
            <div className="relative mt-2">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input
                type="email" required autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-[#E5E7EB] text-[#0A0A0A] pl-10 pr-3 py-3 rounded text-sm focus:border-[#0B1F3A] focus:outline-none"
                placeholder="voce@empresa.com"
                data-testid="login-email-input"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-bold">Senha</label>
            <div className="relative mt-2">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input
                type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-[#E5E7EB] text-[#0A0A0A] pl-10 pr-3 py-3 rounded text-sm focus:border-[#0B1F3A] focus:outline-none"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
          </div>
          <button
            type="submit" disabled={submitting}
            className="w-full flex items-center justify-center gap-2 bg-[#0B1F3A] hover:bg-[#081A32] disabled:opacity-50 transition-colors text-white font-semibold py-3 rounded text-sm tracking-wide"
            data-testid="login-submit-btn"
          >
            {submitting ? "Entrando…" : "Entrar"} <ArrowRight size={16} />
          </button>
        </form>

        <div className="mt-10 text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">
          © {new Date().getFullYear()} Prumo Gestão de Vistorias
        </div>
      </div>

      {/* Right hero */}
      <div
        className="hidden lg:block flex-1 relative border-l border-[#E5E7EB] bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1525367922492-f15fe7b709cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZyUyMGZhY2FkZSUyMGJsYWNrJTIwYW5kJTIwd2hpdGV8ZW58MHx8fHwxNzg0NTk3NjczfDA&ixlib=rb-4.1.0&q=85')" }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A]/95 via-[#0B1F3A]/80 to-[#081A32]/95" />
        <div className="absolute bottom-10 left-10 right-10">
          <div className="text-[10px] uppercase tracking-[0.3em] mb-3 inline-block px-2 py-0.5 bg-[#FFD400] text-[#0B1F3A] font-bold rounded-sm">Multi-tenant · LGPD</div>
          <div className="font-display font-bold text-2xl leading-tight max-w-sm text-white">
            Isolamento rigoroso de dados entre imobiliárias, cidades e perfis.
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-white/70">
            <Building2 size={14} className="text-[#FFD400]" /> 6 cidades · múltiplas imobiliárias
          </div>
        </div>
      </div>
    </div>
  );
}
